import { Dialog } from '@base-ui/react/dialog';
import { useState } from 'react';

import { setStoredActiveOrgSlug } from '../active-org.ts';
import {
  useAuth,
  useCreateOrg,
  useInstallationRepos,
  useLinkRepoToOrg,
  useOrgStatus,
  useStartInstall,
  type Installation,
  type InstallationRepo,
  type OrgSummary,
} from '../api.ts';

import { Select } from './Select.tsx';

/**
 * "Repos available to connect" surface, shared by the Repositories
 * settings page and the snapshot empty states. For each connected
 * GitHub App installation it lists the repos the install can see that
 * aren't linked yet, with a "Link" action.
 *
 * Linking prompts for a **destination org** every time (see
 * `LinkRepoDialog`): add the repo to an existing org (rolling it into
 * that org's shared platform map) or spin up a new org for it (keeping
 * its platform map separate). This is what stops an unrelated repo from
 * being silently fused into the active org's catalog. GitHub install
 * tokens are minted statelessly from the install id, so linking across
 * orgs works.
 *
 * `defaultOpen` expands each installation's repo list on mount — handy
 * in the empty-state context where the repos should be visible without
 * a click; the settings page leaves them collapsed.
 */
export function LinkableRepos({
  orgSlug,
  defaultOpen = false,
}: {
  orgSlug: string;
  defaultOpen?: boolean;
}) {
  const status = useOrgStatus(orgSlug);
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];

  if (status.isPending) {
    return <p className="muted">Loading…</p>;
  }
  if (status.isError) {
    return <p style={{ color: '#c0392b' }}>Error: {status.error.message}</p>;
  }
  const { installations } = status.data;
  if (installations.length === 0) {
    return <ConnectGitHubPrompt orgSlug={orgSlug} />;
  }

  return (
    <div className="stack">
      {installations.map((i) => (
        <AvailableReposForInstall
          key={i.id}
          orgSlug={orgSlug}
          orgs={orgs}
          install={i}
          defaultOpen={defaultOpen}
        />
      ))}
    </div>
  );
}

function AvailableReposForInstall({
  orgSlug,
  orgs,
  install,
  defaultOpen,
}: {
  orgSlug: string;
  orgs: OrgSummary[];
  install: Installation;
  defaultOpen: boolean;
}) {
  const repos = useInstallationRepos(orgSlug, install.installation_id);
  if (repos.isPending) {
    return <p className="muted">Loading repos for {install.github_account_login}…</p>;
  }
  if (repos.isError) {
    return (
      <p style={{ color: '#c0392b' }}>
        Error loading repos for {install.github_account_login}: {repos.error.message}
      </p>
    );
  }
  const unlinked = repos.data.filter((r) => !r.is_linked);
  if (unlinked.length === 0) {
    return (
      <p className="muted">
        All repos accessible to <code>{install.github_account_login}</code> are already linked.
      </p>
    );
  }
  return (
    <details open={defaultOpen}>
      <summary>
        <code>{install.github_account_login}</code>{' '}
        <span className="muted">· {unlinked.length} unlinked</span>
      </summary>
      <ul className="bare stack" style={{ marginTop: '0.5rem' }}>
        {unlinked.map((r) => (
          <li key={r.full_name}>
            <AvailableRepoRow
              defaultOrgSlug={orgSlug}
              orgs={orgs}
              installationId={install.installation_id}
              repo={r}
            />
          </li>
        ))}
      </ul>
    </details>
  );
}

function AvailableRepoRow({
  defaultOrgSlug,
  orgs,
  installationId,
  repo,
}: {
  defaultOrgSlug: string;
  orgs: OrgSummary[];
  installationId: number;
  repo: InstallationRepo;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '0.4rem 0',
        borderBottom: '1px solid rgba(127,127,127,0.1)',
      }}
    >
      <div>
        <code>{repo.full_name}</code>
        {repo.private ? <span className="muted"> · private</span> : null}
        <span className="muted"> · {repo.default_branch}</span>
      </div>
      <LinkRepoDialog
        defaultOrgSlug={defaultOrgSlug}
        orgs={orgs}
        installationId={installationId}
        repo={repo}
      />
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  zIndex: 50,
};

const popupStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'var(--bg, #fff)',
  color: 'var(--ink, inherit)',
  border: '1px solid rgba(127,127,127,0.3)',
  borderRadius: '8px',
  padding: '1.25rem',
  width: 'min(28rem, 92vw)',
  zIndex: 51,
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};

/**
 * Prompt shown when linking a repo: choose an existing org (append to
 * its shared platform map) or create a new org for the repo (separate
 * platform map). Performs the link against the chosen org and switches
 * the active org there so the onboarding banner + resulting data show up
 * where the repo landed.
 */
function LinkRepoDialog({
  defaultOrgSlug,
  orgs,
  installationId,
  repo,
}: {
  defaultOrgSlug: string;
  orgs: OrgSummary[];
  installationId: number;
  repo: InstallationRepo;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'existing' | 'new'>(orgs.length > 0 ? 'existing' : 'new');
  const [existingSlug, setExistingSlug] = useState(defaultOrgSlug);
  const [newName, setNewName] = useState('');
  const link = useLinkRepoToOrg();
  const createOrg = useCreateOrg();
  const busy = link.isPending || createOrg.isPending;

  const linkTo = (orgSlug: string) => {
    link.mutate(
      {
        orgSlug,
        installation_id: installationId,
        owner: repo.owner,
        name: repo.name,
        default_branch: repo.default_branch,
        // Capture the org/owner icon at link time for the nav switcher.
        ...(repo.avatar_url ? { avatar_url: repo.avatar_url } : {}),
        ingest_mode: 'rest_only',
      },
      {
        onSuccess: () => {
          // Land in the org the repo went to so its onboarding banner +
          // data surface where the user just linked it.
          setStoredActiveOrgSlug(orgSlug);
          setOpen(false);
        },
      },
    );
  };

  const onConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'new') {
      const name = newName.trim();
      if (!name) return;
      createOrg.mutate({ name }, { onSuccess: (org) => linkTo(org.slug) });
    } else {
      if (!existingSlug) return;
      linkTo(existingSlug);
    }
  };

  const confirmDisabled = busy || (mode === 'new' ? !newName.trim() : !existingSlug);
  const errorMessage = link.error?.message ?? createOrg.error?.message ?? null;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger className="btn">Link</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop style={backdropStyle} />
        <Dialog.Popup style={popupStyle}>
          <Dialog.Title style={{ marginTop: 0 }}>
            Link <code>{repo.full_name}</code>
          </Dialog.Title>
          <Dialog.Description className="muted" style={{ marginTop: '0.25rem' }}>
            Where should this repo&apos;s data live?
          </Dialog.Description>
          <form onSubmit={onConfirm} className="stack" style={{ marginTop: '1rem' }}>
            <label
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'flex-start',
                opacity: orgs.length === 0 ? 0.5 : 1,
              }}
            >
              <input
                type="radio"
                name="dest"
                checked={mode === 'existing'}
                onChange={() => setMode('existing')}
                disabled={orgs.length === 0}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <strong>Add to an existing org</strong>
                <br />
                <span className="muted">Rolls this repo into that org&apos;s shared platform map.</span>
                {mode === 'existing' && orgs.length > 0 ? (
                  <div style={{ marginTop: '0.4rem' }}>
                    <Select
                      label="Destination org"
                      value={existingSlug}
                      onChange={(e) => setExistingSlug(e.target.value)}
                    >
                      {orgs.map((o) => (
                        <option key={o.slug} value={o.slug}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}
              </span>
            </label>

            <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="radio"
                name="dest"
                checked={mode === 'new'}
                onChange={() => setMode('new')}
                style={{ marginTop: '0.2rem' }}
              />
              <span style={{ width: '100%' }}>
                <strong>Create a new org</strong>
                <br />
                <span className="muted">Keeps this repo&apos;s platform map separate.</span>
                {mode === 'new' ? (
                  <div style={{ marginTop: '0.4rem' }}>
                    <input
                      type="text"
                      placeholder="New org name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      autoFocus
                      style={{ width: '100%' }}
                    />
                  </div>
                ) : null}
              </span>
            </label>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Dialog.Close className="btn" disabled={busy}>
                Cancel
              </Dialog.Close>
              <button type="submit" className="btn" disabled={confirmDisabled}>
                {busy ? 'Linking…' : 'Link repo'}
              </button>
            </div>
            {errorMessage ? (
              <p style={{ color: '#c0392b', margin: 0 }}>{errorMessage}</p>
            ) : null}
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ConnectGitHubPrompt({ orgSlug }: { orgSlug: string }) {
  const startInstall = useStartInstall(orgSlug);
  return (
    <div className="stack">
      <p className="muted">
        No GitHub App installations yet. Connect one to see which repos are available for
        linking. (Repos can also be added via local skill / MCP / manual flows — see the
        Integrations page.)
      </p>
      <p>
        <button
          type="button"
          className="btn"
          disabled={startInstall.isPending}
          onClick={() =>
            startInstall.mutate(
              {},
              {
                onSuccess: ({ install_url }) => {
                  window.location.href = install_url;
                },
              },
            )
          }
        >
          Connect GitHub
        </button>
      </p>
      {startInstall.isError ? (
        <p style={{ color: '#c0392b' }}>{startInstall.error.message}</p>
      ) : null}
    </div>
  );
}
